import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Container, 
  Typography, 
  Paper, 
  IconButton,
  Button,
  TextField,
  InputAdornment,
  AppBar,
  Toolbar,
  Grid,
  Chip,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle
} from '@mui/material';
import { useNavigate, Link } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SearchIcon from '@mui/icons-material/Search';
import DeleteIcon from '@mui/icons-material/Delete';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EmailIcon from '@mui/icons-material/Email';
import VisibilityIcon from '@mui/icons-material/Visibility';
import FilterListIcon from '@mui/icons-material/FilterList';
import PhoneIcon from '@mui/icons-material/Phone';

// Mock data for contact form submissions
const mockContactSubmissions = [
  {
    id: 1,
    name: 'Kimani Waweru',
    email: 'kimani.waweru@example.com',
    phone: '(+254) 712-345-678',
    message: 'I am looking for a property management company to handle my 3 rental properties in the downtown area. Would like to discuss your services and pricing options. I have previously worked with another company but was not satisfied with their level of communication. Please contact me at your earliest convenience.',
    date: 'Mar 28, 2025',
    time: '09:45 AM',
    viewed: false,
    service: 'Property Management'
  },
  {
    id: 2,
    name: 'Akinyi Onyango',
    email: 'akinyi.o@example.com',
    phone: '(+254) 723-987-654',
    message: 'Interested in your luxury renovation services for my penthouse. I am planning a complete remodel including kitchen, bathrooms, and living areas. My budget is around $250,000 and I would like to discuss design options and timeline. Please contact me to schedule a consultation.',
    date: 'Mar 27, 2025',
    time: '03:22 PM',
    viewed: true,
    service: 'Project Management'
  },
  {
    id: 3,
    name: 'Mutua Kioko',
    email: 'mutua.k@example.com',
    phone: '(+254) 735-456-789',
    message: 'Looking for information about your MRI tax support services. What documentation do I need to provide? I own several investment properties and need assistance with organizing my tax documents for the upcoming filing season. Please let me know what your services include and your rates.',
    date: 'Mar 26, 2025',
    time: '11:15 AM',
    viewed: true,
    service: 'MRI Tax Support'
  },
  {
    id: 4,
    name: 'Wambui Njoroge',
    email: 'wambui.n@example.com',
    phone: '(+254) 722-234-567',
    message: 'I need assistance with tenant screening and placement for my duplex property. The current tenants are moving out next month, and I want to ensure we find reliable replacements quickly. Do you offer tenant screening services as a standalone option?',
    date: 'Mar 25, 2025',
    time: '02:30 PM',
    viewed: false,
    service: 'Tenant Management'
  },
  {
    id: 5,
    name: 'Omondi Otieno',
    email: 'omondi.o@example.com',
    phone: '(+254) 733-876-543',
    message: 'I recently purchased a 10-unit apartment building and need a property management company to handle all aspects of management. I live out of state and require full-service management including rent collection, maintenance, and tenant relations. Please provide information about your services and fees.',
    date: 'Mar 24, 2025',
    time: '10:15 AM',
    viewed: true,
    service: 'Property Management'
  },
  {
    id: 6,
    name: 'Njeri Kamau',
    email: 'njeri.k@example.com',
    phone: '(+254) 724-345-678',
    message: 'Interested in your property marketing services. I have a luxury condo that has been vacant for two months and need help with marketing and finding qualified tenants. Would like to know more about your photography, listing, and tenant screening processes.',
    date: 'Mar 23, 2025',
    time: '04:45 PM',
    viewed: false,
    service: 'Property Marketing'
  },
  {
    id: 7,
    name: 'Kipchoge Kiptoo',
    email: 'kipchoge.k@example.com',
    phone: '(+254) 711-567-890',
    message: 'I need help with rent collection and accounting for my 5 rental properties. Currently managing them myself but finding it difficult to keep track of payments and expenses. Looking for a solution that provides clear monthly reports and handles late payment notifications.',
    date: 'Mar 22, 2025',
    time: '01:30 PM',
    viewed: true,
    service: 'Rent Collection'
  }
];

const AdminContacts = () => {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [contacts, setContacts] = useState(mockContactSubmissions);
  const [selectedContact, setSelectedContact] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [anchorEl, setAnchorEl] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [openDialog, setOpenDialog] = useState(false);
  const [contactToDelete, setContactToDelete] = useState(null);
  const [replyDialogOpen, setReplyDialogOpen] = useState(false);
  const [replyEmail, setReplyEmail] = useState({
    to: '',
    subject: '',
    body: ''
  });
  
  useEffect(() => {
    // Check if user is authenticated as admin
    const adminAuth = localStorage.getItem('isAdminAuthenticated');
    if (adminAuth !== 'true') {
      // Redirect to admin login if not authenticated
      navigate('/admin-login');
    } else {
      setIsAuthenticated(true);
    }
  }, [navigate]);

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleContactClick = (contact) => {
    // Mark as viewed if it was not viewed
    if (!contact.viewed) {
      const updatedContacts = contacts.map(c => 
        c.id === contact.id ? { ...c, viewed: true } : c
      );
      setContacts(updatedContacts);
    }
    setSelectedContact(contact);
  };

  const handleBackToList = () => {
    setSelectedContact(null);
  };

  const handleDeleteClick = (contact) => {
    setContactToDelete(contact);
    setOpenDialog(true);
  };

  const handleDeleteConfirm = () => {
    // In a real app, this would delete the contact from the database
    const updatedContacts = contacts.filter(contact => contact.id !== contactToDelete.id);
    setContacts(updatedContacts);
    if (selectedContact && selectedContact.id === contactToDelete.id) {
      setSelectedContact(null);
    }
    setOpenDialog(false);
    setContactToDelete(null);
  };

  const handleDeleteCancel = () => {
    setOpenDialog(false);
    setContactToDelete(null);
  };

  const handleReplyClick = (contact) => {
    setReplyEmail({
      to: contact.email,
      subject: `Re: Contact Form Submission - ${contact.service}`,
      body: `Dear ${contact.name},\n\nThank you for contacting Lantena Property Management regarding our ${contact.service} services.\n\n`
    });
    setReplyDialogOpen(true);
  };

  const handleReplyChange = (e) => {
    const { name, value } = e.target;
    setReplyEmail(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleReplySend = () => {
    // In a real app, this would send the email via an API
    alert('Reply sent successfully!');
    setReplyDialogOpen(false);
  };

  // Filter contacts based on search term
  const filteredContacts = contacts.filter(contact => {
    return searchTerm === '' || 
      contact.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      contact.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      contact.message.toLowerCase().includes(searchTerm.toLowerCase()) ||
      contact.service.toLowerCase().includes(searchTerm.toLowerCase());
  });

  if (!isAuthenticated) {
    return null; // Don't render anything while checking authentication
  }

  return (
    <Box sx={{ backgroundColor: '#fbfbfb', minHeight: '100vh' }}>
      <AppBar position="static" sx={{ backgroundColor: '#0a233b' }}>
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            component={Link}
            to="/dashboard"
            sx={{ mr: 2 }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            Contact Form Submissions
          </Typography>
          <TextField
            placeholder="Search submissions..."
            size="small"
            variant="outlined"
            value={searchTerm}
            onChange={handleSearchChange}
            sx={{
              backgroundColor: 'rgba(255, 255, 255, 0.15)',
              borderRadius: 1,
              width: 250,
              '& .MuiOutlinedInput-root': {
                '& fieldset': {
                  borderColor: 'transparent',
                },
                '&:hover fieldset': {
                  borderColor: 'transparent',
                },
                '&.Mui-focused fieldset': {
                  borderColor: 'transparent',
                },
              },
              '& .MuiInputBase-input': {
                color: 'white',
              }
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: 'white' }} />
                </InputAdornment>
              ),
            }}
          />
          <IconButton color="inherit" onClick={handleMenuOpen}>
            <MoreVertIcon />
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
          >
            <MenuItem onClick={() => { handleMenuClose(); navigate('/admin-emails'); }}>Email Center</MenuItem>
            <MenuItem onClick={() => { handleMenuClose(); navigate('/dashboard'); }}>Back to Dashboard</MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      <Container maxWidth="xl" sx={{ mt: 3, mb: 5 }}>
        {!selectedContact ? (
          // Contact List View
          <Paper elevation={2} sx={{ borderRadius: 2 }}>
            <Box sx={{ 
              p: 2, 
              display: 'flex', 
              justifyContent: 'space-between',
              alignItems: 'center',
              borderBottom: '1px solid #e0e0e0'
            }}>
              <Typography variant="h6" sx={{ color: '#0a233b' }}>
                All Contact Form Submissions
              </Typography>
              <Box>
                <Button
                  variant="text"
                  startIcon={<FilterListIcon />}
                  sx={{ color: '#0a233b', mr: 1 }}
                >
                  Filter
                </Button>
                <Button
                  variant="contained"
                  component={Link}
                  to="/admin-emails/compose"
                  startIcon={<EmailIcon />}
                  sx={{
                    backgroundColor: '#f66b10',
                    '&:hover': { backgroundColor: '#e05a00' }
                  }}
                >
                  Compose Email
                </Button>
              </Box>
            </Box>
            
            <TableContainer>
              <Table sx={{ minWidth: 650 }}>
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                    <TableCell sx={{ fontWeight: 'bold', color: '#0a233b' }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', color: '#0a233b' }}>Name</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', color: '#0a233b' }}>Email</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', color: '#0a233b' }}>Phone</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', color: '#0a233b' }}>Service</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', color: '#0a233b' }}>Date</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', color: '#0a233b' }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredContacts
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((contact) => (
                      <TableRow 
                        key={contact.id}
                        sx={{ 
                          '&:hover': { backgroundColor: '#f5f5f5' },
                          backgroundColor: contact.viewed ? 'inherit' : '#fff4e5',
                          cursor: 'pointer'
                        }}
                        onClick={() => handleContactClick(contact)}
                      >
                        <TableCell>
                          {contact.viewed ? (
                            <Chip 
                              label="Viewed" 
                              size="small" 
                              sx={{ 
                                backgroundColor: '#e0e0e0',
                                color: '#6a6565'
                              }} 
                            />
                          ) : (
                            <Chip 
                              label="New" 
                              size="small" 
                              sx={{ 
                                backgroundColor: '#f66b10',
                                color: 'white'
                              }} 
                            />
                          )}
                        </TableCell>
                        <TableCell sx={{ fontWeight: contact.viewed ? 'normal' : 'bold' }}>
                          {contact.name}
                        </TableCell>
                        <TableCell>{contact.email}</TableCell>
                        <TableCell>{contact.phone}</TableCell>
                        <TableCell>{contact.service}</TableCell>
                        <TableCell>{contact.date}</TableCell>
                        <TableCell>
                          <IconButton 
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleContactClick(contact);
                            }}
                            sx={{ color: '#0a233b' }}
                          >
                            <VisibilityIcon />
                          </IconButton>
                          <IconButton 
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleReplyClick(contact);
                            }}
                            sx={{ color: '#0a233b' }}
                          >
                            <EmailIcon />
                          </IconButton>
                          <IconButton 
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteClick(contact);
                            }}
                            sx={{ color: '#0a233b' }}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
            
            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={filteredContacts.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </Paper>
        ) : (
          // Contact Detail View
          <Paper elevation={2} sx={{ borderRadius: 2 }}>
            <Box sx={{ 
              p: 2, 
              display: 'flex', 
              alignItems: 'center',
              borderBottom: '1px solid #e0e0e0'
            }}>
              <IconButton 
                edge="start" 
                onClick={handleBackToList}
                sx={{ color: '#0a233b', mr: 1 }}
              >
                <ArrowBackIcon />
              </IconButton>
              <Typography variant="h6" sx={{ flexGrow: 1, color: '#0a233b' }}>
                Contact Form Submission Details
              </Typography>
              <Button
                variant="contained"
                startIcon={<EmailIcon />}
                onClick={() => handleReplyClick(selectedContact)}
                sx={{
                  backgroundColor: '#f66b10',
                  '&:hover': { backgroundColor: '#e05a00' },
                  mr: 2
                }}
              >
                Reply
              </Button>
              <IconButton 
                onClick={() => handleDeleteClick(selectedContact)}
                sx={{ color: '#0a233b' }}
              >
                <DeleteIcon />
              </IconButton>
            </Box>
            
            <Box sx={{ p: 3 }}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                  <Card elevation={0} sx={{ backgroundColor: '#f5f5f5', height: '100%' }}>
                    <CardContent>
                      <Typography variant="h6" sx={{ mb: 2, color: '#0a233b' }}>
                        Contact Information
                      </Typography>
                      
                      <Box sx={{ mb: 3 }}>
                        <Typography variant="subtitle2" color="text.secondary">
                          Full Name
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                          {selectedContact.name}
                        </Typography>
                      </Box>
                      
                      <Box sx={{ mb: 3 }}>
                        <Typography variant="subtitle2" color="text.secondary">
                          Email Address
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <EmailIcon sx={{ fontSize: 18, mr: 1, color: '#0a233b' }} />
                          <Typography variant="body1">
                            {selectedContact.email}
                          </Typography>
                        </Box>
                      </Box>
                      
                      <Box sx={{ mb: 3 }}>
                        <Typography variant="subtitle2" color="text.secondary">
                          Phone Number
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <PhoneIcon sx={{ fontSize: 18, mr: 1, color: '#0a233b' }} />
                          <Typography variant="body1">
                            {selectedContact.phone}
                          </Typography>
                        </Box>
                      </Box>
                      
                      <Box sx={{ mb: 3 }}>
                        <Typography variant="subtitle2" color="text.secondary">
                          Service Interested In
                        </Typography>
                        <Typography variant="body1">
                          {selectedContact.service}
                        </Typography>
                      </Box>
                      
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary">
                          Submission Date & Time
                        </Typography>
                        <Typography variant="body1">
                          {selectedContact.date} at {selectedContact.time}
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
                
                <Grid item xs={12} md={8}>
                  <Box>
                    <Typography variant="h6" sx={{ mb: 2, color: '#0a233b' }}>
                      Message
                    </Typography>
                    <Paper 
                      elevation={0} 
                      sx={{ 
                        p: 3, 
                        backgroundColor: '#f5f5f5',
                        minHeight: '200px'
                      }}
                    >
                      <Typography 
                        variant="body1" 
                        sx={{ 
                          whiteSpace: 'pre-line',
                          lineHeight: 1.7
                        }}
                      >
                        {selectedContact.message}
                      </Typography>
                    </Paper>
                    
                    <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end' }}>
                      <Button
                        variant="outlined"
                        onClick={handleBackToList}
                        sx={{
                          borderColor: '#a6d1ea',
                          color: '#0a233b',
                          '&:hover': {
                            borderColor: '#0a233b',
                            backgroundColor: 'rgba(166, 209, 234, 0.1)'
                          },
                          mr: 2
                        }}
                      >
                        Back to List
                      </Button>
                      <Button
                        variant="contained"
                        startIcon={<EmailIcon />}
                        onClick={() => handleReplyClick(selectedContact)}
                        sx={{
                          backgroundColor: '#f66b10',
                          '&:hover': { backgroundColor: '#e05a00' }
                        }}
                      >
                        Reply to {selectedContact.name}
                      </Button>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Paper>
        )}
      </Container>
      
      {/* Delete Confirmation Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleDeleteCancel}
      >
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the contact submission from {contactToDelete?.name}? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} sx={{ color: '#0a233b' }}>
            Cancel
          </Button>
          <Button 
            onClick={handleDeleteConfirm} 
            variant="contained"
            sx={{
              backgroundColor: '#f66b10',
              '&:hover': { backgroundColor: '#e05a00' }
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Reply Email Dialog */}
      <Dialog
        open={replyDialogOpen}
        onClose={() => setReplyDialogOpen(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Reply to Contact</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="To"
            name="to"
            value={replyEmail.to}
            onChange={handleReplyChange}
            margin="normal"
            variant="outlined"
            InputProps={{
              readOnly: true,
            }}
          />
          
          <TextField
            fullWidth
            label="Subject"
            name="subject"
            value={replyEmail.subject}
            onChange={handleReplyChange}
            margin="normal"
            variant="outlined"
          />
          
          <TextField
            fullWidth
            label="Message"
            name="body"
            value={replyEmail.body}
            onChange={handleReplyChange}
            margin="normal"
            variant="outlined"
            multiline
            rows={10}
          />
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setReplyDialogOpen(false)} 
            sx={{ color: '#0a233b' }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleReplySend} 
            variant="contained"
            startIcon={<EmailIcon />}
            sx={{
              backgroundColor: '#f66b10',
              '&:hover': { backgroundColor: '#e05a00' }
            }}
          >
            Send Reply
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminContacts;
